import classNames from "classnames";
import "./TripadvisorRating.scss";

type HalfDotProps = {
	size: number;
} & React.SVGProps<SVGSVGElement>;

const HalfDot = ({ size, ...props }: HalfDotProps) => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 12 12"
			fill="none"
			aria-hidden="true"
			{...props}
		>
			<g clipPath="url(#clip0_6083_6329)">
				<path
					d="M6 0.700012C8.9 0.700012 11.3 3.10001 11.3 6.00001C11.3 8.90001 8.9 11.3 6 11.3C3.1 11.3 0.699997 8.90001 0.699997 6.00001C0.699997 3.10001 3.1 0.700012 6 0.700012Z"
					fill="white"
				/>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M11.8 6.00001C11.8 2.80001 9.2 0.200012 6 0.200012C2.8 0.200012 0.199997 2.80001 0.199997 6.00001C0.199997 9.20001 2.8 11.8 6 11.8C9.2 11.8 11.8 9.20001 11.8 6.00001ZM6 10.8V1.20001C8.6 1.20001 10.8 3.30001 10.8 6.00001C10.8 8.70001 8.7 10.8 6 10.8Z"
					fill="#00A680"
				/>
			</g>
			<defs>
				<clipPath id="clip0_6083_6329">
					<rect width={size} height={size} fill="white" />
				</clipPath>
			</defs>
		</svg>
	);
};

type FullDotProps = {
	size: number;
} & React.SVGProps<SVGSVGElement>;

const FullDot = ({ size, ...props }: FullDotProps) => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 12 12"
			fill="none"
			aria-hidden="true"
			{...props}
		>
			<g clipPath="url(#clip0_6083_6324)">
				<path
					d="M6 11.8C2.8 11.8 0.199997 9.20001 0.199997 6.00001C0.199997 2.80001 2.8 0.200012 6 0.200012C9.2 0.200012 11.8 2.80001 11.8 6.00001C11.8 9.20001 9.2 11.8 6 11.8Z"
					fill="#00A680"
				/>
			</g>
			<defs>
				<clipPath id="clip0_6083_6324">
					<rect width={size} height={size} fill="white" />
				</clipPath>
			</defs>
		</svg>
	);
};

type EmptyDotProps = {
	size: number;
} & React.SVGProps<SVGSVGElement>;

const EmptyDot = ({ size, ...props }: EmptyDotProps) => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 12 12"
			fill="none"
			aria-hidden="true"
			{...props}
		>
			<g clipPath="url(#clip0_6083_6326)">
				<path
					d="M6 0.700012C8.9 0.700012 11.3 3.10001 11.3 6.00001C11.3 8.90001 8.9 11.3 6 11.3C3.1 11.3 0.699997 8.90001 0.699997 6.00001C0.699997 3.10001 3.1 0.700012 6 0.700012Z"
					fill="white"
				/>
				<path
					d="M6 11.8C2.8 11.8 0.199997 9.20001 0.199997 6.00001C0.199997 2.80001 2.8 0.200012 6 0.200012C9.2 0.200012 11.8 2.80001 11.8 6.00001C11.8 9.20001 9.2 11.8 6 11.8ZM6 1.20001C3.3 1.20001 1.2 3.40001 1.2 6.00001C1.2 8.60001 3.3 10.8 6 10.8C8.7 10.8 10.8 8.70001 10.8 6.00001C10.8 3.30001 8.6 1.20001 6 1.20001Z"
					fill="#00A680"
				/>
			</g>
			<defs>
				<clipPath id="clip0_6083_6326">
					<rect width={size} height={size} fill="white" />
				</clipPath>
			</defs>
		</svg>
	);
};

type TripadvisorRatingProps = {
	rating?: number;
	maxRate?: number;
	size?: number;
	className?: string;
};

export const TripadvisorRating = ({
	rating = 0,
	maxRate = 5,
	size = 12,
	className,
}: TripadvisorRatingProps) => {
	const clampedRating = Math.max(0, Math.min(maxRate, rating));

	const tripadvisorRatingClassName = classNames("tripadvisor-rating-container", className);

	const fullDots = Math.floor(clampedRating);
	const hasHalfDot = clampedRating - fullDots >= 0.5;
	const emptyDots = maxRate - fullDots - (hasHalfDot ? 1 : 0);

	return (
		<div
			className={tripadvisorRatingClassName}
			role="img"
			aria-label={`Note ${clampedRating} sur ${maxRate}`}
			style={{ display: "flex", gap: "2px" }}
		>
			{Array.from({ length: fullDots }, (_, index) => (
				<FullDot size={size} key={`full-${index}`} />
			))}

			{hasHalfDot && <HalfDot size={size} />}

			{Array.from({ length: emptyDots }, (_, index) => (
				<EmptyDot key={`empty-${index}`} size={size} />
			))}
		</div>
	);
};
